import { SelectorEntities } from 'shared/entities/components/Selector';
import { TFunctionType } from 'shared/entities/localization';
import { IField } from 'shared/entities/form';
import { AreaSize } from 'shared/entities/geometry';

import { INewList } from '../list';

import { AttachmentKind, FileTags, FileType } from './server';

export type MediaFileWithPreview = FileType.image | FileType.video;

export const isMediaPreviewType = (
  fileType: FileType
): fileType is MediaFileWithPreview =>
  fileType === FileType.image || fileType === FileType.video;

export enum FileSizeType {
  kb = 'KByte',
  mb = 'MByte'
}

export const mediaFileTypes = [
  FileType.image,
  FileType.music,
  FileType.video,
  FileType.voice
];

export interface IFileBase {
  id: string;
  fileType: FileType;
  title: string;
  preview: string | null;
  size: number;
  date: Date;
  url: string;
  extension: string;
  extra: any;
  areaSize: AreaSize | null;
  duration: number | null;
}

export interface IFile extends IFileBase {
  availability: FileTags;
}

export interface IBlockAttachments {
  files: INewList<IBlockAttachment | IBlockVarAttachment>;
}

export interface IAttachment extends IFileBase {
  readonly asDocument: IField<boolean>;
}

export interface IBlockAttachmentsModel extends IBlockAttachments {
  addAttachments(attachments: IFile[], asDocument: boolean): void;
  onRemove(ids: string[]): void;
  remove(id: string): void;
}

export interface IBlockAttachment extends IAttachment {
  readonly attachmentId: string;
  readonly kind: AttachmentKind.attach;
}

export const mapFileExtensionToFileType: Record<string, FileType> = {
  doc: FileType.doc,
  docx: FileType.doc,
  mp3: FileType.music,
  png: FileType.image,
  jpg: FileType.image,
  jpeg: FileType.image,
  svg: FileType.image,
  gif: FileType.image,
  webp: FileType.image,
  bmp: FileType.image,
  heic: FileType.image,
  tiff: FileType.image,
  mp4: FileType.video,
  avi: FileType.video,
  mov: FileType.video,
  webm: FileType.video,
  wmv: FileType.video,
  mkv: FileType.video
};

export const mapExactMimeTypeToFileType: Record<string, FileType> = {
  'application/pdf': FileType.other,
  'application/msword': FileType.doc,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    FileType.doc,
  'application/vnd.ms-excel': FileType.table,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    FileType.table
};

/** Расширения видео, которые могут быть воспроизведены браузером в теге <video> */
export const browserPlayableVideoExtensions = ['mp4', 'webm', 'mov'];

export const browserPlayableAudioExtensions = [
  'mp3',
  'wav',
  'm4a',
  'mp4',
  'aac',
  'ogg',
  'oga',
  'opus',
  'webm'
];

export const mapFileTypeToLabel: Record<FileType, string> = {
  [FileType.all]: 'DOC',
  [FileType.doc]: 'DOC',
  [FileType.image]: 'IMG',
  [FileType.music]: 'MP3',
  [FileType.video]: 'MP4',
  [FileType.sticker]: 'STK',
  [FileType.voice]: 'OGG',
  [FileType.other]: 'FILE',
  [FileType.table]: 'XLS',
  [FileType.site]: 'URL'
};

export const sizeTypesOrder = [FileSizeType.kb, FileSizeType.mb];

export const sizeTypesEntities: SelectorEntities<FileSizeType> =
  sizeTypesOrder.reduce<SelectorEntities<FileSizeType>>(
    (acc, size) => ({
      ...acc,
      [size]: {
        id: size,
        title: (t: TFunctionType): string =>
          t(`file.size.${size}`, {
            ns: 'entities'
          })
      }
    }),
    {} as SelectorEntities<FileSizeType>
  );
export const typeMultiplier = {
  [FileSizeType.kb]: 1,
  [FileSizeType.mb]: 1024
};

export interface IVarAttachmentBase {
  id: string;
  value: string;
  readonly kind: AttachmentKind.expr;
}

export interface IBlockVarAttachment extends IVarAttachmentBase {
  readonly asDocument: IField<boolean>;
}

/**
 * Почему менеджер не может загрузить фото ВКонтакте по ссылке. Повторяет проверки бэкенда
 * в `attachments/upload_by_vk_url`: в проекте нужен VK-канал, а у менеджера — личный
 * VK-токен, который выдаётся, только если он сам подключал сообщество ВКонтакте.
 */
export enum VkPhotoByUrlBlocker {
  noVkChannel = 'noVkChannel',
  noManagerToken = 'noManagerToken'
}
